import type { WorkflowState } from "fond/hooks/useCustomWorkflow";

import {
  BASE_DATA_LAYER_KEYS,
  BASE_DATA_LAYERS,
  BaseDataLayerKey,
  BaseDataMissingLayers,
  BaseDataPresence,
  GenerationKey,
  REQUIRED_INPUT_KEYS,
} from "./layers";
import { Staleness } from "./staleness";

export type BaseDataGatingInput = {
  present: BaseDataPresence;
  /** Planner layers of each base data layer that have no data; names the missing half of partly added layers. */
  missing?: BaseDataMissingLayers;
  staleness: Staleness;
  /** Generation workflows currently starting or in progress. */
  running: readonly GenerationKey[];
  /** Whether a generation run is being prepared, before its workflow starts. */
  starting: boolean;
  /** Base data rows with a running upload, import, delete or save (see `getBusyBaseDataKeys`). */
  busy: readonly BaseDataLayerKey[];
  hasArchitecture: boolean;
  readOnly: boolean;
};

export type Gate = { enabled: true; reason: null } | { enabled: false; reason: string };

/** Tools offered by the Modify data flyout. */
export type ModifyDataAction = "editLayers" | "pathPreferences" | "reviewParcels";

/** Whether a base data row's data can be changed now. */
export type BaseDataRowGates = {
  /** Its options: import, upload, draw and generate. */
  change: Gate;
  delete: Gate;
};

export type BaseDataGating = {
  /** Whether each generation workflow can run now, given its dependencies. */
  generate: Record<GenerationKey, Gate>;
  /** Whether running generation can be cancelled. */
  cancel: Gate;
  /** Whether each Modify data tool can be used now. */
  modify: Record<ModifyDataAction, Gate>;
  rows: Record<BaseDataLayerKey, BaseDataRowGates>;
  /** What each generation workflow would generate its layer from, given the layers present. */
  generatedFrom: Record<GenerationKey, string>;
  /** Whether base data is being generated, or a generation run is starting. */
  isGenerating: boolean;
  /** Whether the base data is ready for design. */
  isComplete: boolean;
};

export const OPEN: Gate = { enabled: true, reason: null };
const closed = (reason: string): Gate => ({ enabled: false, reason });

const READ_ONLY = closed("You have read-only access to this version");
const GENERATING = closed("Base data is being generated");
const BUSY = closed("Wait for the running task to finish");
const DELETE_BUSY = closed("Can't delete while a task is running");

const isGenerating = ({ running, starting }: BaseDataGatingInput): boolean => running.length > 0 || starting;

/** Return why no base data can change now, while read-only or generating, or null. */
const getIdleGate = (input: BaseDataGatingInput): Gate | null => {
  if (input.readOnly) return READ_ONLY;
  if (isGenerating(input)) return GENERATING;
  return null;
};

/** Return whether a custom workflow is starting or in progress. */
export const isWorkflowRunning = (state: WorkflowState): boolean => state === "STARTING" || state === "PENDING" || state === "IN_PROGRESS";

/** Return the items as an English list, e.g. "a, b and c". */
export const formatList = (items: string[]): string =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/** Return the names of a base data layer's planner layers that have no data, e.g. ["points"]. */
export const getMissingLayerNames = (key: BaseDataLayerKey, missing: readonly string[]): string[] => {
  return BASE_DATA_LAYERS[key].layers.flatMap(({ id, name }) => (missing.includes(id) ? [name] : []));
};

/** Return the missing half of a partly added multi-layer row with its prefix, e.g. "underground points", or null unless partly added. */
const getMissingHalf = (key: "ug" | "aerial", prefix: string, { present, missing }: BaseDataGatingInput): string | null => {
  const missingLayers = missing?.[key] ?? [];
  if (present[key] || missingLayers.length === 0 || missingLayers.length === BASE_DATA_LAYERS[key].layers.length) return null;
  return `${prefix} ${formatList(getMissingLayerNames(key, missingLayers))}`;
};

const getNoNetworkReason = (input: BaseDataGatingInput): string => {
  const ugHalf = getMissingHalf("ug", "underground", input);
  const aerialHalf = getMissingHalf("aerial", "aerial", input);
  if (ugHalf && aerialHalf) return `Add ${ugHalf} or ${aerialHalf} first`;
  if (ugHalf) return `Add ${ugHalf}, or aerial spans & poles, first`;
  if (aerialHalf) return `Add ${aerialHalf}, or underground path & points, first`;
  return "Add underground path or aerial spans first";
};

/** Return why base data can't be worked on now, while read-only, generating or any row is busy, or null. */
const getUnbusyGate = (input: BaseDataGatingInput): Gate | null => getIdleGate(input) ?? (input.busy.length > 0 ? BUSY : null);

const commonGate = (input: BaseDataGatingInput): Gate | null => {
  const gate = getUnbusyGate(input);
  if (gate) return gate;
  const missing = REQUIRED_INPUT_KEYS.filter((key) => !input.present[key]).map((key) => BASE_DATA_LAYERS[key].name.toLowerCase());
  if (missing.length > 0) return closed(`Add ${formatList(missing)} first`);
  return null;
};

const getModifyGates = (input: BaseDataGatingInput): Record<ModifyDataAction, Gate> => {
  const { present, staleness } = input;
  const idle = getUnbusyGate(input) ?? OPEN;
  const pathPreferences = (() => {
    if (!idle.enabled) return idle;
    if (!present.ug) return closed(`Add ${getMissingHalf("ug", "underground", input) ?? "underground path & points"} first`);
    if (staleness.ugStale) return closed("Generate underground path again first");
    return OPEN;
  })();
  return { editLayers: idle, pathPreferences, reviewParcels: commonGate(input) ?? OPEN };
};

const getRowGates = (input: BaseDataGatingInput): Record<BaseDataLayerKey, BaseDataRowGates> => {
  const idle = getIdleGate(input);
  return Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => {
      const isBusy = input.busy.includes(key);
      return [key, { change: idle ?? (isBusy ? BUSY : OPEN), delete: idle ?? (isBusy ? DELETE_BUSY : OPEN) }];
    })
  ) as Record<BaseDataLayerKey, BaseDataRowGates>;
};

const getUgGate = (input: BaseDataGatingInput): Gate => {
  const gate = commonGate(input);
  if (gate) return gate;
  if (!input.hasArchitecture) return closed("Set an architecture first");
  return OPEN;
};

const getLeadinsGate = (input: BaseDataGatingInput): Gate => {
  const gate = commonGate(input);
  if (gate) return gate;
  const { present, staleness } = input;
  if (present.aerial || (present.ug && !staleness.ugStale)) return OPEN;
  if (present.ug) return closed("Generate underground path again first");
  return closed(getNoNetworkReason(input));
};

const getLeadinsGeneratedFrom = ({ ug, aerial }: BaseDataPresence): string => {
  const sources = ["streets", ...(ug ? ["path", "points"] : []), ...(aerial ? ["spans", "poles"] : [])];
  return `Created from ${formatList(sources)}`;
};

/** Return which base data generation controls are available and whether design can begin. */
export const getBaseDataGating = (input: BaseDataGatingInput): BaseDataGating => {
  const { present, staleness, running, readOnly } = input;
  const generate: Record<GenerationKey, Gate> = { ug: getUgGate(input), leadins: getLeadinsGate(input) };

  const isComplete =
    REQUIRED_INPUT_KEYS.every((key) => present[key]) &&
    (present.ug || present.aerial) &&
    present.leadins &&
    !staleness.ugStale &&
    !staleness.leadinsStale &&
    running.length === 0;

  return {
    generate,
    cancel: readOnly ? READ_ONLY : OPEN,
    modify: getModifyGates(input),
    rows: getRowGates(input),
    generatedFrom: { ug: "Created from streets and parcels", leadins: getLeadinsGeneratedFrom(present) },
    isGenerating: isGenerating(input),
    isComplete,
  };
};
